import { useEffect, useState } from 'react'

// Services
import {
  useDeleteMediaFiles,
  useUploadAssetMedia,
  useGetAssetMediaFiles,
} from 'lib/apollo/hooks'

// Types
import {
  DuplicateGroup,
  Image,
  MediaFile,
  MediaUploadProps,
  PreviewModalProps,
} from './MediaUpload.types'

// Utils
import fileToBase64 from 'lib/utils/fileToBase64'

// Other
import { v4 as uuidv4 } from 'uuid'
import { useParams } from 'react-router-dom'
import { enqueueSnackbar } from 'notistack'

const useMediaUploadLogic = ({
  fileNames,
  assetId,
  scanId,
  isLocal,
  hasActions,
  isHandledLocally,
  onAddMedia,
  onDeleteMedia,
  onLocalMediaFilesChange,
  onMediaFilesChange,
  setIsMediaActionTriggered,
}: MediaUploadProps) => {
  const { surgeryId } = useParams()

  // Services
  const { data: media, loading: isLoadingFileNames } = useGetAssetMediaFiles(
    fileNames ?? []
  )
  const [deleteFiles, { loading: isLoadingDeleteFiles }] = useDeleteMediaFiles({
    surgeryId: surgeryId ?? '',
  })
  const [uploadFiles, { loading: isLoadingUploadFiles }] = useUploadAssetMedia()

  // States
  const [images, setImages] = useState<Image[]>([])
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isPreviewModalOpen, setIsPreviewModalOpen] =
    useState<PreviewModalProps>({ isOpen: false })
  const [deletedImageName, setDeletedImageName] = useState<string>()
  const [modalId, setModalId] = useState<string>(uuidv4())
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState<{
    isOpen: boolean
    fileName?: string
  }>()

  // Add new state for tracking duplicate groups
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([])

  const isEmpty = images.length < 1
  const showMediaBlock = hasActions ? true : !isEmpty ? true : false

  // Handlers
  const handleSaveImages = async (newImages: File[]) => {
    if (!isHandledLocally) {
      const uploadPayloads = await Promise.all(
        newImages.map(async (img) => {
          const base64Content = await fileToBase64(img as File)
          return {
            filename: img.name,
            mimetype: img.type,
            encoding: 'base64',
            content: base64Content,
          }
        })
      )

      if (isLocal) {
        setImages((prev) => [
          ...prev,
          ...newImages.map((file) => ({
            preview: URL.createObjectURL(file),
            isLocal: false,
            file: file,
          })),
        ])

        setIsUploadModalOpen(false)
        setModalId(uuidv4())

        const filteredUploadPayloads = uploadPayloads.filter(
          (payload) => payload !== null
        ) as MediaFile[]

        if (onLocalMediaFilesChange) {
          onLocalMediaFilesChange(filteredUploadPayloads, 'add')
        }
        return
      }

      if (uploadPayloads.length === 0) return

      try {
        const response = await uploadFiles({
          variables: {
            assetId: assetId,
            scanId: scanId,
            files: uploadPayloads,
          },
        })

        if (
          response.data?.uploadAssetMedia?.files &&
          response.data?.uploadAssetMedia?.files.length > 0
        ) {
          const uploadedFiles = response.data.uploadAssetMedia.files.map(
            (file) => ({
              preview: file.base64Content,
              isLocal: false,
              file: new File([], file.filename),
            })
          )

          setImages((prevImages) => [
            ...prevImages.filter((img) => !img.isLocal),
            ...uploadedFiles,
          ])

          setIsMediaActionTriggered && setIsMediaActionTriggered(true)
          setIsUploadModalOpen(false)
          setModalId(uuidv4())

          enqueueSnackbar('Files uploaded successfully.', {
            variant: 'success',
          })

          // This is used to pass the uploaded files as mediaFiles in the scan in BATCH_ADD_ASSETS_TO_SURGERY mutation.
          onMediaFilesChange &&
            onMediaFilesChange(
              [...uploadedFiles.map((item) => item.file.name)],
              'add'
            )
        } else {
          enqueueSnackbar('Failed to upload files.', {
            variant: 'error',
          })
        }
      } catch (error) {
        console.error('Error uploading files:', error)
        enqueueSnackbar('Failed to upload files.', {
          variant: 'error',
        })
      }
    } else {
      setImages((prev) => [
        ...prev,
        ...newImages.map((file) => ({
          preview: URL.createObjectURL(file),
          isLocal: true,
          file: file,
        })),
      ])
      setIsUploadModalOpen(false)
      setModalId(uuidv4())
      if (onAddMedia) {
        const uploadPayloads: MediaFile[] = await Promise.all(
          newImages.map(async (img) => {
            const base64Content = await fileToBase64(img as File)
            return {
              filename: img.name,
              mimetype: img.type,
              encoding: 'base64',
              content: base64Content,
              action: 'add',
            }
          })
        )
        onAddMedia(uploadPayloads)
      }
    }
  }

  const handleDeleteImage = async (fileName?: string) => {
    if (!fileName) return

    // Find all related filenames in the duplicate group
    const group = duplicateGroups.find((g) =>
      g.images.some((img) => img.file?.name === fileName)
    )
    const fileNamesToDelete = (group?.images
      .map((img) => img.file?.name)
      .filter(Boolean) as string[]) || [fileName]

    if (!isHandledLocally) {
      if (isLocal) {
        setImages((prev) => {
          const updatedImages = prev.filter(
            (item) => item.file?.name !== fileName
          )
          enqueueSnackbar('Image has been deleted', {
            variant: 'success',
          })
          if (onLocalMediaFilesChange) {
            const updatedFiles = updatedImages.map(async (item) => {
              const base64Content = await fileToBase64(item.file as File)
              return {
                filename: item.file?.name || '',
                mimetype: item.file?.type || '',
                encoding: 'base64',
                content: base64Content || '',
              }
            })

            Promise.all(updatedFiles).then((files) => {
              onLocalMediaFilesChange(files, 'delete')
            })
          }

          return updatedImages
        })

        setIsDeleteAlertOpen({ isOpen: false, fileName: undefined })
        return
      }

      setDeletedImageName(fileName)
      setIsDeleteAlertOpen({ isOpen: false, fileName: undefined })

      deleteFiles({
        variables: {
          assetId: assetId,
          scanId: scanId,
          filenames: fileNamesToDelete,
        },
      })
        .then((res) => {
          if (res.data?.deleteMediaFiles.status === 'Media deleted') {
            enqueueSnackbar('Image(s) have been deleted', {
              variant: 'success',
            })

            // Remove all duplicates from the images state
            setImages((prev) =>
              prev.filter(
                (item) => !fileNamesToDelete.includes(item.file?.name || '')
              )
            )

            setIsMediaActionTriggered && setIsMediaActionTriggered(true)
            onMediaFilesChange &&
              onMediaFilesChange(fileNamesToDelete, 'delete')
          }
        })
        .catch(() => {
          enqueueSnackbar(
            'Something went wrong while deleting the images. Please try again.',
            {
              variant: 'error',
            }
          )
        })
        .finally(() => {
          setDeletedImageName(undefined)
        })
    } else {
      setImages((prev) =>
        prev.filter(
          (item) => !fileNamesToDelete.includes(item.file?.name || '')
        )
      )
      setIsDeleteAlertOpen({ isOpen: false, fileName: undefined })
      // Call onDeleteMedia for each filename
      fileNamesToDelete.forEach((name) => onDeleteMedia?.(name))
    }
  }

  useEffect(() => {
    if (!media?.getMediaFiles.length) return

    const processImages = () => {
      const imageGroups = new Map<string, Image[]>()

      for (const file of media.getMediaFiles) {
        // Some DTM assets have a content URL that is the same for all images
        const url = file.content

        const image = {
          isLocal: false,
          preview: file.content,
          file: { name: file.filename },
        } as Image

        const existing = imageGroups.get(url) || []
        imageGroups.set(url, [...existing, image])
      }

      // Convert Map to array of DuplicateGroups
      const groups = Array.from(imageGroups.entries()).map(([url, images]) => ({
        url, // Using URL as identifier
        images,
      }))

      setDuplicateGroups(groups)
      // Set images array with only the first image from each group
      setImages(groups.map((group) => group.images[0]))
    }

    processImages()
  }, [media])

  useEffect(() => {
    if (fileNames?.length === 0) {
      setImages([])
    }
  }, [fileNames])

  return {
    images,
    modalId,
    isEmpty,
    hasActions,
    showMediaBlock,
    deletedImageName,
    isUploadModalOpen,
    isPreviewModalOpen,
    isLoadingFileNames,
    isLoadingDeleteFiles,
    isLoadingUploadFiles,
    isDeleteAlertOpen,
    setIsDeleteAlertOpen,
    setIsUploadModalOpen,
    handleSaveImages,
    handleDeleteImage,
    setIsPreviewModalOpen,
  }
}

export default useMediaUploadLogic
